import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Form, Button, Modal } from "react-bootstrap";
import { useSubmitCancellationRequest } from "../../api/mutations/useSubmitCancellationRequest";
import { useAppContext } from "../../Context/AppContext";
import { CANCELLATION_REASONS } from "../../types/Types";

type Step = "form" | "confirm" | "success";

const CancelMembership: React.FC = () => {
  const navigate = useNavigate();
  const { objectId } = useAppContext();
  const [step, setStep] = useState<Step>("form");
  const [reasonId, setReasonId] = useState<number | "">("");
  const [improvementFeedback, setImprovementFeedback] = useState("");
  const [subReason, setSubReason] = useState("");

  const { mutate: submitRequest, isPending } = useSubmitCancellationRequest({
    onSuccess: () => setStep("success"),
    onError: () => {},
  });

  const handleRequestCancellation = () => {
    if (reasonId === "" || !objectId || subReason === "") return;
    setStep("confirm");
  };

  const handleConfirm = () => {
    if (reasonId === "" || !objectId || subReason === "") return;

    const combinedFeedback = [subReason, improvementFeedback.trim()]
      .filter(Boolean)
      .join(" | ");

    submitRequest({
      objectId,
      reasonId: Number(reasonId),
      improvementFeedback: combinedFeedback || undefined,
    });
  };

  const handleCancelConfirm = () => setStep("form");

  return (
    <div className="p-4 pt-5 pt-md-5 m-0">
      <div className="card shadow-lg border-0 p-4">
        <Button
          variant="link"
          className="p-0 mb-3 text-decoration-none"
          onClick={() => navigate(-1)}
        >
          ← Back
        </Button>
        <h4 className="fw-semibold mb-4">Cancel Membership</h4>

        {step === "form" && (
          <>
            <p className="text-muted mb-3">
              Why are you cancelling your membership?
            </p>
            <Form.Group className="mb-3">
              <Form.Select
                value={reasonId === "" ? "" : String(reasonId)}
                onChange={(e) => {
                  const value = e.target.value === "" ? "" : Number(e.target.value);
                  setReasonId(value);
                  setSubReason("");
                }}
                required
              >
                <option value="">Select a reason...</option>
                {CANCELLATION_REASONS.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {reasonId !== "" && (
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">
                  Please tell us a bit more
                </Form.Label>
                <div className="d-flex flex-column gap-1">
                  {CANCELLATION_REASONS.find((r) => r.id === reasonId)
                    ?.detail.split("/")
                    .map((option) => option.trim())
                    .filter(Boolean)
                    .map((option) => (
                      <Form.Check
                        key={option}
                        type="radio"
                        id={`sub-reason-${reasonId}-${option}`}
                        name="sub-reason"
                        label={option}
                        value={option}
                        checked={subReason === option}
                        onChange={(e) => setSubReason(e.currentTarget.value)}
                      />
                    ))}
                </div>
              </Form.Group>
            )}
            <Form.Group className="mb-4">
              <Form.Label>Anything you&apos;d like us to improve?</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Optional feedback"
                value={improvementFeedback}
                onChange={(e) => setImprovementFeedback(e.target.value)}
              />
            </Form.Group>
            <Button
              variant="danger"
              onClick={handleRequestCancellation}
              disabled={reasonId === "" || subReason === ""}
            >
              Request cancellation
            </Button>
          </>
        )}

        {step === "confirm" && (
          <Modal show centered onHide={handleCancelConfirm}>
            <Modal.Header closeButton>
              <Modal.Title>Confirm cancellation</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              Are you sure you want to request cancellation?
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCancelConfirm}>
                Cancel
              </Button>
              <Button variant="danger" onClick={handleConfirm} disabled={isPending}>
                {isPending ? "Submitting..." : "Confirm"}
              </Button>
            </Modal.Footer>
          </Modal>
        )}

        {step === "success" && (
          <Modal show centered onHide={() => navigate("/manage-membership")}>
            <Modal.Header closeButton>
              <Modal.Title>Request received</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              We are sorry to see you go. Your cancellation request has been
              received and will be processed within 48 hours. A member of the
              SMBF team will reach out to you within that time to discuss your
              reasons for leaving.
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="primary"
                onClick={() => navigate("/manage-membership")}
              >
                Close
              </Button>
            </Modal.Footer>
          </Modal>
        )}
      </div>
    </div>
  );
};

export default CancelMembership;
